import { Injectable } from '@angular/core';
import { NonprofitAdminRequestDisplayModel } from '@core/models/claim.model';
import { MyNonprofitsDataExceptRequestsForUser } from '@core/models/npo.model';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { MyNonprofitsResources } from './my-nonprofits.resources';
import { MyNonprofitsState } from './my-nonprofits.state';
import { ChangeRoleToAdministratorAPI } from '@core/typings/ui/change-role-to-administrator.typing';
import { NotifierService } from '@yourcause/common/notifier';
import { Router } from '@angular/router';
import { I18nService } from '@yourcause/common/i18n';
import { firstValueFrom } from 'rxjs';
import { Address, AddressFormatterService } from '@yourcause/common';

@AttachYCState(MyNonprofitsState)
@Injectable({ providedIn: 'root' })
export class MyNonprofitsService extends BaseYCService<MyNonprofitsState>{

  constructor (
    private myNonprofitsResources: MyNonprofitsResources,
    private formatter: AddressFormatterService,
    private notifierService: NotifierService,
    private i18nService: I18nService,
    private router: Router
  ) {
    super();
  }
    get requestsForUser () {
      return this.get('requestsForUser');
    }

    get useParentDescription () {
      return this.get('useParentDescription');
    }

    get useChapterDescription () {
      return this.get('useChapterDescription');
    }

    get tableRows () {
      return this.get('tableRows');
    }

    setRequestsForUser (allNonprofitAdminRequests: NonprofitAdminRequestDisplayModel[]) {
      this.set('requestsForUser', allNonprofitAdminRequests);
    }

    setMyNonprofitsStateDataExceptRequestsForUser (myNonprofitsStateDataExceptRequestsForUser: MyNonprofitsDataExceptRequestsForUser) {
      this.set('tableRows', myNonprofitsStateDataExceptRequestsForUser.tableRows);
      this.set('useParentDescription', myNonprofitsStateDataExceptRequestsForUser.useParentDescription);
      this.set('useChapterDescription', myNonprofitsStateDataExceptRequestsForUser.useChapterDescription);
    }

    async getMyNonprofitsStateDataExceptRequestsForUser () {
      const response = await firstValueFrom(this.myNonprofitsResources.getMyNonprofitsStateDataExceptRequestsForUser());

      response.tableRows.forEach(npo => {
        (npo.address = npo.address || {} as Address).toString = () => {
          return this.formatter.format(npo.address, false, true);
        };
      });

      return response;
    }

    async getFilteredMyNonprofitRows (searchText: string, searchType: number) {
      const response = await firstValueFrom(this.myNonprofitsResources.getMyNonprofitsStateDataExceptRequestsForUser(searchText, searchType));

      response.tableRows.forEach(npo => {
        (npo.address = npo.address || {} as Address).toString = () => {
          return this.formatter.format(npo.address, false, true);
        };
      });

      return response.tableRows;
    }

    async getMyNonprofitChaptersAndChapterRequestsForNonprofit (nonprofitId: number) {
      const response = await firstValueFrom(this.myNonprofitsResources.getMyNonprofitChaptersAndChapterRequestsForNonprofit(
        nonprofitId
      ));

      return response;
    }

    async cancelChapterRequest (
      chapterRequestIds: number[]
    ) {
      const response = this.myNonprofitsResources.cancelChapterRequest(chapterRequestIds);

      return response;
    }

    async setAllDataForMyNonprofitRows () {
      const [
        myNonprofitsStateDataExceptRequestsForUser,
        requestsForUsers
      ] = await Promise.all([
        await this.getMyNonprofitsStateDataExceptRequestsForUser(),
        await this.getRequestsForUser()
      ]);
      this.setRequestsForUser(requestsForUsers);
      this.setMyNonprofitsStateDataExceptRequestsForUser(myNonprofitsStateDataExceptRequestsForUser);
    }

    async changeRoleToAdministrator (nonprofitId: number, comment: string) {
      try {
        const payload : ChangeRoleToAdministratorAPI = {
          nonprofitId: nonprofitId,
          comments: comment
        };

        await this.myNonprofitsResources.changeRole(
          payload
        );

        this.notifierService.success(this.i18nService.translate(
          'nonprofits:notificationSuccessfullyRequestedToChangeRoleToAdministrator',
          {},
          'Successfully requested to change role to administrator'
        ));

      } catch (e: any) {
        this.handleChangeRoleToAdministratorErrorCodes(e);

      }
    }

    private async getRequestsForUser () {
      const response = await this.myNonprofitsResources.getRequestsForUser();

      return response;
    }

    private handleChangeRoleToAdministratorErrorCodes (exception: any) {
      let notifierMessage = this.i18nService.translate(
        'nonprofits:unknownErrorNotificationChangeRoleToAdministrator',
        {},
        'There was an error changing role to administrator'
      );

      if (exception?.error?.errorCode && exception.error.errorCode === 'Does_Not_Exist') {
        this.router.navigateByUrl('/support-request?itemId=2');
      } else {
        if (exception?.status === 404) {
          notifierMessage = this.i18nService.translate(
            'nonprofits:notFoundErrorNotificationChangeRoleToAdministrator',
            {},
            'There was an error changing role to administrator; nonprofit not found'
          );
        } else if (exception?.status === 403) {
          if (exception?.message === 'User is not assigned to this nonprofit.') {
            notifierMessage = this.i18nService.translate(
              'nonprofits:unassignedErrorNotificationChangeRoleToAdministrator',
              {},
              'There was an error changing role to administrator; not assigned to nonprofit'
            );
          } else if (exception?.message === 'User is not allowed to change their role.') {
            notifierMessage = this.i18nService.translate(
              'nonprofits:notAllowedErrorNotificationChangeRoleToAdministrator',
              {},
              'There was an error changing role to administrator; not allowed to change role'
            );
          } else if (exception?.message === 'User is an administrator for this nonprofit.') {
            notifierMessage = this.i18nService.translate(
              'nonprofits:notificationSuccessfullyRequestedToChangeRoleToAdministratorExisting',
              {},
              'You are already an administrator'
            );
          }
        }

        this.notifierService.error(notifierMessage);

        return;
      }
    }
}
